import classNames from 'classnames';
import type { ComponentProps } from 'react';
import React, { useLayoutEffect, useRef } from 'react';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import useOpenManager from '../utils/popover/useOpenManager';
import useTriggerElement from '../utils/popover/useTriggerElement';
import ErrorBoundary from './ErrorBoundary';
import * as styles from './Popover.module.scss';
import Slot from './Slot';
import type { TetherRef } from './Tether';
import Tether from './Tether';

type PopoverProps = Pick<
  ComponentProps<typeof Tether>,
  'placement' | 'flipBehavior' | 'shiftBehavior' | 'offset' | 'portalContainer'
> & {
  anchorElement?: HTMLElement | null;
  children: React.ReactNode;
  view: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * A headless component that displays a view when clicking on its children.
 * Both the view and the anchor (children) must be able to accept props and pass them
 * down to their underlying HTML elements, since they will receive refs, event handlers, etc.
 *
 * @example
 * <Popover view={<FloatingCard>Hello</FloatingCard>} placement="top">
 *   <Button>Click me</Button>
 * </Popover>
 */
const Popover = ({
  anchorElement: providedAnchorElement,
  children,
  view,
  open,
  onOpenChange,
  placement,
  flipBehavior,
  shiftBehavior,
  offset,
  portalContainer,
}: PopoverProps) => {
  const { isOpen, hasBeenOpened, onIsOpenChange } = useOpenManager({
    open,
    onOpenChange,
  });

  const tetherRef = useRef<TetherRef>(null);
  useLayoutEffect(() => {
    if (isOpen) tetherRef.current?.updatePositionedView();
  }, [isOpen]);

  const [triggerElement, setTriggerElement] = useTriggerElement();

  const onClickOutside = (e: Event) => {
    if (!(e.target instanceof Node) || !triggerElement?.contains(e.target)) {
      onIsOpenChange(false);
    }
  };

  const anchorElement = providedAnchorElement ?? triggerElement;

  return (
    <>
      <Slot
        ref={setTriggerElement}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => {
          onIsOpenChange(!isOpen);
        }}
      >
        {children}
      </Slot>
      <ErrorBoundary name="Popover">
        <Tether
          ref={tetherRef}
          anchorElement={anchorElement}
          positionedView={
            hasBeenOpened && (
              <ClickOutsideHandler
                asChild
                onClickOutside={isOpen ? onClickOutside : undefined}
              >
                <Slot
                  aria-hidden={!isOpen}
                  className={classNames(styles.floating, {
                    [styles.isClosed]: !isOpen,
                  })}
                >
                  {view}
                </Slot>
              </ClickOutsideHandler>
            )
          }
          flipBehavior={flipBehavior}
          shiftBehavior={shiftBehavior}
          placement={placement}
          offset={offset}
          portalContainer={portalContainer}
        />
      </ErrorBoundary>
    </>
  );
};

export default Popover;
